export enum FileTransferStateType {
  /**
   * Default idle state of file transfer, transfer dialog is closed at this state
   */
  NotStarted = 'notStarted',
  /**
   * This is the regular state when the file transfer dialog is open and while user selects files
   */
  SelectingFiles = 'selectingFiles',
  /**
   * Provider Only: a file transfer request has been sent to the patient
   */
  RequestSent = 'requestSent',
  /**
   * Patient only: a file transfer request has been received from the provider
   */
  RequestReceived = 'requestReceived',
  /**
   * The receiver has rejected the request to upload files
   */
  RequestRejected = 'requestRejected',
  /**
   * Switches to this state once the user hits "send files" button and the upload begins
   */
  Uploading = 'uploading',
  /**
   * Sender Only
   * State called when all files have been uploaded successfully
   */
  UploadComplete = 'uploadComplete',
  /**
   * State the receiver gets when the sender has cancelled the upload
   */
  RemoteUploadCancelled = 'remoteUploadCancelled',
  /**
   * Receiver Only
   * State called when we receive a DownloadReady realtime message
   * indicating that every file in the FT session has been uploaded successfully
   */
  FilesReadyForDownload = 'filesReadyForDownload',
  /**
   * Indicates that the receiver is downloading the files
   */
  RemoteDownloading = 'remoteDownloading',
  /**
   * Indicates to the sender that the receiver has successfully downloaded the files
   */
  RemoteDownloadCompleted = 'remoteDownloadCompleted',
  /**
   * Indicates to the receiver that they have successfully downloaded the files
   */
  LocalDownloadCompleted = 'localDownloadCompleted',
  /**
   * Indicates that the current user is downloading the files
   */
  Downloading = 'downloading',
  /**
   * Indicates that the current user has successfully downloaded the files
   */
  DownloadCompleted = 'downloadCompleted',
  /**
   * The receiver has cancelled the download of the files you have sent
   */
  DownloadCancelled = 'downloadCancelled',
  /**
   * The sender has cancelled the download of the files you have just received
   */
  RemoteDownloadCancelled = 'remoteDownloadCancelled',
  /**
   * Received an error from the server
   */
  ServerError = 'serverError',
}

export enum FileTransferEventName {
  /**
   * Message sent from the provider to request files from the patient
   */
  Request = 'request',
  /**
   * Sent from the server to the provider when a single file is successfully uploaded and returns a download url
   * If the patient is the uploader,they will receive this message from the provider instead
   * since currently the server can only send realtime messages to the provider via userId
   */
  FileAvailable = 'fileAvailable',
  /**
   * Sent to the patient when the provider cancels the request
   */
  CancelRequest = 'cancelRequest',
  /**
   * Sent to the provider when patient rejects a request to upload files
   */
  RejectRequest = 'rejectRequest',
  /**
   * Sent from the sender to indicate that all the files are ready for download
   * it contains an array of TransferredFile objects
   */
  DownloadReady = 'downloadReady',
  /**
   * Sent from the receiver to indicate that the download has been initiated
   */
  DownloadInProgress = 'downloadInProgress',
  /**
   * Sent from the receiver to indicate that all the files have been downloaded
   */
  DownloadCompleted = 'downloadCompleted',
  /**
   * Sent from the receiver to indicate that download has been cancelled
   */
  DownloadCancelled = 'downloadCancelled',
  /**
   * Sent by the uploader to notify that they have stopped the upload
   */
  UploadCancelled = 'uploadCancelled',
  /**
   * Sent by the provider to the patient to stop them from downloading files already sent
   */
  DownloadStoppedRemotely = 'stopDownloadRemotely',
}

// only if eventName === FileTransferEventName.DownloadReady
export type DownloadReadyMessagePayload = {
  filesReady: Record<string, DownloadableFile>;
};

// only if eventName === FileTransferEventName.FileAvailable
export type FileAvailableMessagePayload = {
  fileReady: { downloadUrl: string; name: string };
  realtimeMemberId?: string;
  userId?: string;
  fileTransferSessionId?: string;
  fileUuid: string;
  uploadStatus: 'ready' | 'failed';
};

// only if eventName === FileTransferEventName.Request
export type RequestMessagePayload = {
  fileTransferSessionId: string;
};

// Each realtime event has a specific payload type
export type FileTransferEventData = {
  [FileTransferEventName.Request]: RequestMessagePayload;
  [FileTransferEventName.FileAvailable]: FileAvailableMessagePayload;
  [FileTransferEventName.CancelRequest]: undefined;
  [FileTransferEventName.RejectRequest]: undefined;
  [FileTransferEventName.DownloadReady]: DownloadReadyMessagePayload;
  [FileTransferEventName.DownloadInProgress]: undefined;
  [FileTransferEventName.DownloadCompleted]: undefined;
  [FileTransferEventName.DownloadCancelled]: undefined;
  [FileTransferEventName.UploadCancelled]: undefined;
  [FileTransferEventName.DownloadStoppedRemotely]: undefined;
};

export type FileTransferEventPayload = {
  eventName: FileTransferEventName;
  eventData: FileTransferEventData[FileTransferEventName];
};

export type DownloadableFile = TransferredFile & {
  downloadUrl: string;
  isDownloaded: boolean;
};

export type TransferredFile = {
  name: string;
  size: number;
  type: string;
};

export type FileTransferSliceState = {
  currentState: FileTransferStateType;
  fileTransferSessionId?: string;
  filesAvailable: Record<string, DownloadableFile>;
  filesUploaded: TransferredFile[];
  requestMemberIds: string[];
  uploadingFilesCount: number;
};
